import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Calendar, Loader2, RefreshCw, Upload, Download, CheckCircle2, Clock, CalendarDays, Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ScheduleEditor } from "../assignments/components/ScheduleEditor";
import { GoogleSvg } from "@/lib/svg";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { useCalendarSync } from "@/lib/calendarSync";
import { useAuth } from "@/lib/auth";

interface GoogleCalendar {
    id: string;
    summary: string;
    backgroundColor: string;
    shouldSync?: boolean;
}

const CalendarPullSettings = () => {


    const { useRerenderListener, getOptions, updateOptions, callExtensionFunc } = useCommunication();

    const { handleDisconnectCalendar, ensureBetterCampusCalendar, handleInitialSync, listCalendars } = useCalendarSync();

    const { options } = useRerenderListener(["google_cal_sync_token", "google_cal_last_sync", "google_cal_push_settings", "google_calendars"]);

    const handleCalendarToggle = async (calendarId: string, checked: boolean) => {
        const options = getOptions("sync");
        const calendar = options.google_calendars[calendarId];

        updateOptions({
            google_calendars: {
                ...options.google_calendars,
                [calendarId]: { ...calendar, shouldSync: checked },
            },
        });
        // The useGoogleCalendarEvents hook will automatically detect this change
        // and re-sync due to useRerenderListener watching 'google_calendars'
    };


    const hasActivePullCalendar = Object.values(options?.google_calendars || {}).some((calendar: any) => calendar.shouldSync);

    const planType = options?.auth?.user?.app_metadata?.plan_type || "free";
    const isFreePlan = ["none", "free"].includes(planType as any);

    const [calendarsLoading, setCalendarsLoading] = useState(false);

    useEffect(() => {
        (async (): Promise<void> => {
            setCalendarsLoading(true);
            await listCalendars();
            setCalendarsLoading(false);
        }
        )();
    }, []);

    return (
        <>

            <div>
                {/*}
                <h3 className="text-sm font-semibold mb-3 text-base-0 flex items-center gap-2 ml-1.5 text-gray-700">
                    <CalendarDays className="w-4 h-4" />
                    Select Calendars to Display
                </h3>
                */}
                <div className="relative">
                    <div className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto">
                        {calendarsLoading && (
                            <div className="col-span-2 text-center py-8 px-4 bg-base-1 rounded-lg border border-base">
                                <Loader2 className="w-8 h-8 animate-spin text-base-0 mx-auto mb-2" />
                                <p className="text-sm text-base-0 opacity-70">Loading calendars...</p>
                            </div>
                        )}
                        {!calendarsLoading && Object.values(options.google_calendars || {}).map((calendar: any) => {
                            console.log("calendar here", calendar);
                            const checked = options.google_calendars[calendar.id].shouldSync;
                            return (
                                <CalendarToggle checked={checked} key={calendar.id} calendar={calendar} onToggle={handleCalendarToggle} />
                            )
                        })}
                        {!calendarsLoading && Object.keys(options.google_calendars || {}).length === 0 && (
                            <div className="text-center py-8 px-4 bg-base-1 rounded-lg border border-base">
                                <Calendar className="w-8 h-8 text-base-0 opacity-40 mx-auto mb-2" />
                                <p className="text-sm text-base-0 opacity-70">No calendars found.</p>
                                <p className="text-xs text-base-0 opacity-50 mt-1">Click "Sync Now" to fetch your calendars.</p>
                            </div>
                        )}
                    </div>
                    {/*
                        <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-page-background to-transparent pointer-events-none z-10" />
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-page-background to-transparent pointer-events-none z-10" />
                        */}
                </div>
            </div>

            <div className={`transition-opacity ${hasActivePullCalendar ? 'opacity-100' : 'opacity-70'}`}>
                <div className="flex items-center justify-between mb-2 mt-2">
                    <h3 className="text-sm font-semibold text-base-0 flex items-center gap-2 text-gray-700 ml-1.5">
                        <CheckCircle2 className="w-4 h-4 text-gray-700" />
                        What gets synced
                    </h3>
                </div>
                <div className="border border-base rounded-xl bg-base-1">

                    <div className="flex items-start gap-3 p-4">
                        <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#00a4ff] text-white">
                            <Check className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-base-0 block">Google Events</span>
                            <p className="text-xs text-base-0 opacity-70 mt-1">
                                Events from your Google Calendar will be displayed in the{" "}
                                <button
                                    onClick={() => {
                                        callExtensionFunc("toggleNavigator", { page: "assignments" });
                                    }}
                                    className={`underline hover:no-underline font-medium cursor-pointer hover:opacity-100 text-base-0`}
                                >
                                    assignments page
                                </button>
                            </p>
                        </div>
                    </div>


                </div>

                {/*!hasActivePullCalendar && (
                        <div className="mt-3 p-3 bg-base-1 border border-base rounded-lg">
                            <p className="text-xs text-base-0 opacity-80">
                                <strong>Sync is disabled.</strong> Select at least one calendar to start syncing Google events to BetterCampus.
                            </p>
                        </div>
                    )*/}
            </div>
        </>

    )
}

const CalendarPushSettings = ({ signup = false }: { signup?: boolean }) => {

    const { useRerenderListener, updateOptions, callExtensionFunc } = useCommunication();
    const { planType } = useAuth();
    const { options } = useRerenderListener(["google_cal_push_settings", "display_sync_status"]);
    const { handleInitialSync, ensureBetterCampusCalendar } = useCalendarSync();

    const [syncProgress, setSyncProgress] = useState<{ synced: number; total: number } | null>(null);
    const [isSyncInProgress, setIsSyncInProgress] = useState(false);

    const handlePushSettingToggle = async (setting: string, checked: boolean) => {
        updateOptions({
            google_cal_push_settings: {
                ...options.google_cal_push_settings,
                [setting]: checked,
            },
        });
    };

    const handlePushEventsToggle = async (value: boolean) => {

        updateOptions({
            google_cal_push_settings: {
                ...options.google_cal_push_settings,
                pushEnabled: value,
            },
        });

        if (value) {
            await ensureBetterCampusCalendar();
            await handleInitialSync();
        }
    };

    const toggleNavigator = (page: string) => {
        callExtensionFunc("toggleNavigator", { page: page });
    }

    const toggleScheduleEditor = () => {
        callExtensionFunc("toggleNavigator", { page: "schedule" });
    }

    const isFreePlan = ["none", "free"].includes(planType as any);

    return (

        <>
            {/* Calendar Target Card */}
            <div className="bg-base-1 rounded-xl p-4 border border-base">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-base-0 flex items-center justify-center shadow-sm">
                            <GoogleSvg className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-base-0">Send Events To</h3>
                            <p className="text-xs text-base-0 opacity-70 mt-0.5">BetterCampus Calendar</p>
                        </div>
                    </div>
                    <Switch
                        height={18}
                        variant="default"
                        checked={!!(options.google_cal_push_settings?.pushEnabled)}
                        disabled={isSyncInProgress}
                        onCheckedChange={(value) => handlePushEventsToggle(value)}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between border border-base rounded-xl p-3">
                <p className="text-sm font-medium text-base-0">Display progress when syncing</p>
                <Switch
                    height={18}
                    variant="default"
                    checked={!!(options.display_sync_status ?? true)}
                    onCheckedChange={(value) => updateOptions({ display_sync_status: value })}
                />
            </div>

            <div className={`transition-opacity ${options.google_cal_push_settings?.pushEnabled ? 'opacity-100' : 'opacity-70'}`}>
                <div className="flex items-center justify-between mb-2 mt-2">
                    <h3 className="text-sm font-semibold text-base-0 flex items-center gap-2 text-gray-700 ml-1.5">
                        <CheckCircle2 className="w-4 h-4" />
                        What gets synced
                    </h3>
                    {syncProgress && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-base-1 rounded-lg border border-base">
                            <RefreshCw className="h-3.5 w-3.5 animate-spin text-base-0" />
                            <span className="text-xs font-medium text-base-0">
                                {syncProgress.synced}/{syncProgress.total} synced
                            </span>
                        </div>
                    )}
                </div>
                <div className={`border border-base rounded-xl bg-base-1 py-2 ${options.google_cal_push_settings?.pushEnabled ? '' : 'opacity-50'}`}>

                    <div className="flex items-start gap-3 px-4 py-2">
                        <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#00a4ff]">
                            <Check className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-base-0 block">Planned Items</span>
                            <p className="text-xs text-base-0 opacity-70 mt-1">
                                Items you plan in the{" "}
                                <button
                                    onClick={() => {
                                        if (!isSyncInProgress) {
                                            toggleNavigator("assignments");
                                        }
                                    }}
                                    disabled={isSyncInProgress}
                                    className={`underline hover:no-underline font-medium ${isSyncInProgress ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-100 text-base-0'}`}
                                >
                                    assignments page
                                </button>
                            </p>
                        </div>
                    </div>

                    {/* Due Dates */}
                    <div className="flex items-start gap-3 px-4 py-2">
                        <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#00a4ff]">
                            <Check className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-base-0 block">Assignment Due Dates</span>
                            <p className="text-xs text-base-0 opacity-70 mt-1">Any assignments due are synced</p>
                            {/*}
                            <p className="text-xs text-base-0 opacity-70 mt-1">
                                {!signup && isFreePlan && (
                                    <span className="block mt-1">
                                        <strong>Free plan:</strong> Only assignments due in the next 3 days are synced. Upgrade to unlock full calendar syncing
                                    </span>
                                )}
                                {!signup && !isFreePlan && (
                                    <span className="block mt-1">
                                        <strong>Pro plan:</strong> All assignments for the year are synced
                                    </span>
                                )}
                                {signup && (
                                    <span className="block mt-1">
                                        Due dates in the next 3 days are synced
                                    </span>
                                )}

                            </p>
                              */}
                        </div>
                    </div>

                    {/* Class Times */}
                    <div className="flex items-start gap-3 px-4 py-2">
                        <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#00a4ff] text-white">
                            <Check className="w-3 h-3 text-base-0" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-base-0 block">Class Times</span>
                            <p className="text-xs text-base-0 opacity-70 mt-1">Class times you schedule with BetterCampus</p>
                            {/*}
                            <p className="text-xs text-base-0 opacity-70 mt-1">
                                Scheduled class times{" "}
                                <button
                                    onClick={toggleScheduleEditor}
                                    disabled={isSyncInProgress}
                                    className={`underline hover:no-underline font-medium ${isSyncInProgress ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-100 text-base-0'}`}
                                >
                                    (manage schedule)
                                </button>
                            </p>
                            */}
                        </div>
                    </div>
                </div>

                {/*!options.google_cal_push_settings?.pushEnabled && (
                                                <div className="mt-3 p-3 bg-base-1 border border-base rounded-lg">
                                                    <p className="text-xs text-base-0 opacity-80">
                                                        <strong>Sync is disabled.</strong> Enable the switch above to start syncing these items to your Google Calendar.
                                                    </p>
                                                </div>
                                            )*/}
            </div>

            {/* What Gets Pushed - Informational Only */}

            {/* Push Now Button 
                                        <button onClick={triggerPush} disabled={!options.google_cal_push_settings?.targetCalendar || pushing} className="w-full py-[10px] px-[12px] bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm font-medium">
                                            {pushing ? "Pushing..." : "Push to Google Calendar"}
                                        </button>
                                        */}
        </>

    )

}

export const CalendarSyncSettings = ({ signup = false }: { signup?: boolean }) => {


    const { useRerenderListener, getOptions, updateOptions, callExtensionFunc } = useCommunication();

    const { handleDisconnectCalendar, ensureBetterCampusCalendar, handleInitialSync, listCalendars } = useCalendarSync();

    const [syncing, setSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState<{ synced: number; total: number } | null>(null);
    const [activeTab, setActiveTab] = useState<"pull" | "push">("push");
    const [isProcessing, setIsProcessing] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [showScheduleEditor, setShowScheduleEditor] = useState(false);

    return (
        <>
            <div className="flex flex-col gap-2">
                <>

                    <div className="flex gap-2 p-1 bg-base-2 rounded-xl border border-base">
                        <button
                            onClick={() => setActiveTab("push")}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${activeTab === "push" ? "bg-gray-100 text-black" : "text-gray-500 hover:text-black"}`}
                        >
                            <Upload className="w-4 h-4" />
                            Send to Google
                        </button>
                        <button
                            onClick={() => setActiveTab("pull")}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${activeTab === "pull" ? "bg-gray-100 text-black" : "text-gray-500 hover:text-black"}`}
                        >
                            <Download className="w-4 h-4" />
                            Pull from Google
                        </button>
                    </div>


                    {activeTab === "pull" && <CalendarPullSettings signup={signup} />}
                    {activeTab === "push" && <CalendarPushSettings signup={signup} />}


                    {/* Disconnect Option */}
                    {!signup && (
                        <div className="pt-4 border-t border-base">
                            <button
                                onClick={async () => {
                                    if (confirm("Are you sure you want to disconnect Google Calendar?")) {
                                        setDisconnecting(true);
                                        try {
                                            await handleDisconnectCalendar();
                                        } finally {
                                            setDisconnecting(false);
                                        }
                                    }
                                }}
                                className={`w-full text-sm transition-all flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-red-500 font-medium hover:bg-base-1 border-base bg-base-0 cursor-pointer`}
                            >
                                {disconnecting && <RefreshCw className="h-4 w-4 animate-spin" />}
                                {disconnecting ? "Disconnecting..." : "Disconnect Google Calendar"}
                            </button>
                        </div>
                    )}
                </>
            </div>

            {/* Schedule Editor Dialog */}
            <Dialog open={showScheduleEditor} onOpenChange={setShowScheduleEditor}>
                <DialogContent variant="theme" className="w-max">
                    <ScheduleEditor />
                </DialogContent>
            </Dialog>
        </>
    );
};

const CalendarToggle = ({ checked, calendar, onToggle }: { checked: boolean; calendar: GoogleCalendar; onToggle: (id: string, checked: boolean) => void; disabled?: boolean }) => {
    return (
        <label className={`group flex items-center gap-3 p-3 py-3.5 rounded-xl border transition-all cursor-pointer border-base bg-base-0 hover:border-base hover:shadow-sm ${checked ? 'bg-base-1 border-base' : ''}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onToggle(calendar.id, e.target.checked)}
                className={`w-4 h-4 rounded border-base text-base-0 focus:ring-base-0 cursor-pointer`}
            />
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="size-3 rounded-full flex-shrink-0" style={{ backgroundColor: calendar.backgroundColor }} />
                <span className="text-sm text-gray-800 truncate">{calendar.summary}</span>
            </div>
        </label>
    );
};
